import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';

import {
  ConfigureEventStore,
  convertToFullTemplate
} from 'src/app/features/events/state/configure-events.state';
import { EmailTemplateStore } from 'src/app/shared/services/email-template/state/email-template.state';
import { mergeNotificationEmails } from 'src/app/shared/services/email/notification-email.functions';
import { EventTemplateStore } from 'src/app/shared/services/events/templates/event-templates.state';
import { AxleToastService } from 'src/app/shared/services/toast/axle-toast.service';
import { UserStore } from 'src/app/shared/services/user/user.state';

export const createEventTemplateResolver: ResolveFn<boolean> = () => {
  const configureEventStore = inject(ConfigureEventStore);
  const eventTemplateStore = inject(EventTemplateStore);
  const emailTemplateStore = inject(EmailTemplateStore);
  const userStore = inject(UserStore);
  const router = inject(Router);
  const toast = inject(AxleToastService);

  configureEventStore.resetState({
    configureMode: 'CREATE',
    feature: 'TEMPLATE'
  });

  const navigation = router.currentNavigation();
  const id = navigation?.extras?.state?.['cloneTemplateId'];

  const defaultFromEmail = userStore.currentDefaultFromEmail();
  if (!defaultFromEmail) return false;

  const cachedTemplates = emailTemplateStore.defaultTemplates();

  if (cachedTemplates) {
    const notificationEmails = convertToFullTemplate(
      cachedTemplates,
      defaultFromEmail
    );
    configureEventStore.updateState({ notificationEmails });
  }

  if (id) {
    return new Promise<boolean>((resolve) => {
      const fetchDefaultsPromise = new Promise<void>((resolveDefaults) => {
        if (cachedTemplates) {
          resolveDefaults();
        } else {
          emailTemplateStore.getDefaultNotificationEmails({
            onSuccess: (defaultEmails) => {
              const notificationEmails = convertToFullTemplate(
                defaultEmails,
                defaultFromEmail
              );
              configureEventStore.updateState({ notificationEmails });
              resolveDefaults();
            },
            onFail: () => {
              toast.addError('Failed to get default notification emails');
              resolveDefaults();
            }
          });
        }
      });

      fetchDefaultsPromise.then(() => {
        eventTemplateStore.initializeTemplate({
          id,
          onSuccess: (details) => {
            const currentDefaultEmails =
              configureEventStore.notificationEmails();

            const mergedNotificationEmails = mergeNotificationEmails(
              currentDefaultEmails,
              details.eventTemplateMetadata.notificationEmails
            );

            configureEventStore.initializeTemplate(
              details,
              'TEMPLATE',
              'Copy of ' + details.name
            );

            configureEventStore.updateState({
              notificationEmails: mergedNotificationEmails
            });

            resolve(true);
          },
          onFail: () => {
            toast.addError('Failed to clone template');
            resolve(false);
          }
        });
      });
    });
  } else {
    configureEventStore.getDefaultNotificationEmails();
    return true;
  }
};
